import React from 'react';
import { NavLink } from 'react-router-dom';
import { FiArrowRight, FiChevronDown, FiChevronUp, FiExternalLink, FiFilter } from 'react-icons/fi';
import { Seo } from '../components/Seo';
import { site } from '../config/site';
import { offers } from '../data/offers';
import { Icon } from '../components/ui/Icon';

type SortKey = 'rating' | 'payout' | 'bonus';

function compare(a: number, b: number, dir: 'asc' | 'desc') {
  return dir === 'asc' ? a - b : b - a;
}

export default function ComparisonPage() {
  const [q, setQ] = React.useState('');
  const [sortKey, setSortKey] = React.useState<SortKey>('rating');
  const [dir, setDir] = React.useState<'asc' | 'desc'>('desc');

  const rows = React.useMemo(() => {
    const filtered = offers.filter((o) => {
      const needle = q.trim().toLowerCase();
      if (!needle) return true;
      return (
        o.brand.toLowerCase().includes(needle) ||
        o.tagline.toLowerCase().includes(needle) ||
        o.sports.join(' ').toLowerCase().includes(needle)
      );
    });

    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'rating') return compare(a.rating, b.rating, dir);
      if (sortKey === 'payout') return compare(a.payouts.typicalHours, b.payouts.typicalHours, dir === 'asc' ? 'asc' : 'desc');
      return compare(a.welcome.maxEur, b.welcome.maxEur, dir);
    });

    return sorted;
  }, [q, sortKey, dir]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${site.name} — Comparatif d’offres`,
    url: `${site.url}/comparatif-offres`,
    itemListElement: offers.map((o, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: `${site.url}/offres/${o.slug}`,
      name: o.brand,
    })),
  };

  return (
    <>
      <Seo
        title="Comparatif des offres"
        description="Comparatif d’offres de paris sportifs en France: bonus, vitesse de retrait, sports forts et avis. Trie, filtre, choisis."
        canonicalPath="/comparatif-offres"
        jsonLd={jsonLd}
      />

      <section className="lbf-container py-12 sm:py-16">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl">Comparatif des offres</h1>
            <p className="mt-3 max-w-2xl text-sm text-zinc-200/80">
              Tri par note, bonus ou vitesse de retrait. L’objectif: te donner un choix net, pas une liste interminable.
            </p>
          </div>
          <NavLink to="/offres" className="lbf-btn-ghost">
            Liste des avis <Icon icon={FiArrowRight} aria-hidden="true" />
          </NavLink>
        </div>

        <div className="mt-8 grid gap-4 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="lbf-card p-5">
              <div className="flex items-center gap-2 text-sm font-extrabold text-white">
                <Icon icon={FiFilter} aria-hidden="true" />
                Filtres & tri
              </div>
              <label className="mt-4 grid gap-2 text-sm">
                <span className="font-semibold text-white/90">Recherche</span>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ex: foot, turf, NetBet…"
                  className="rounded-xl border border-white/10 bg-zinc-950 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-fuchsia-400/60"
                />
              </label>

              <div className="mt-5 grid gap-2">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-300/70">Tri</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={sortKey === 'rating' ? 'lbf-btn-primary' : 'lbf-btn-ghost'}
                    onClick={() => setSortKey('rating')}
                  >
                    Note
                  </button>
                  <button
                    type="button"
                    className={sortKey === 'bonus' ? 'lbf-btn-primary' : 'lbf-btn-ghost'}
                    onClick={() => setSortKey('bonus')}
                  >
                    Bonus
                  </button>
                  <button
                    type="button"
                    className={sortKey === 'payout' ? 'lbf-btn-primary' : 'lbf-btn-ghost'}
                    onClick={() => setSortKey('payout')}
                  >
                    Retrait
                  </button>
                </div>
                <button
                  type="button"
                  className="lbf-btn-ghost justify-between"
                  onClick={() => setDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                >
                  Direction: {dir === 'asc' ? 'asc' : 'desc'}{' '}
                  {dir === 'asc' ? (
                    <Icon icon={FiChevronUp} aria-hidden="true" />
                  ) : (
                    <Icon icon={FiChevronDown} aria-hidden="true" />
                  )}
                </button>
              </div>

              <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-zinc-200/85">
                Astuce: si tu hésites, commence par <span className="font-bold text-white">la vitesse de retrait</span>.
                Un bonus ne sert à rien si tu n’aimes pas la sortie.
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            {/* Mobile cards */}
            <div className="grid gap-3 md:hidden">
              {rows.map((o) => (
                <div key={o.slug} className="lbf-card p-5">
                  <div className="flex items-center gap-3">
                    <img
                      src={o.imagePath}
                      alt={o.brand}
                      className="h-11 w-11 rounded-2xl border border-white/10 bg-white/5 object-contain p-1"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-base font-extrabold text-white">{o.brand}</div>
                      <div className="truncate text-xs text-zinc-300/80">{o.tagline}</div>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-2 text-sm">
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                      <div className="text-xs text-zinc-300/80">Bonus</div>
                      <div className="mt-1 font-extrabold text-white">{o.welcome.maxEur}€</div>
                      <div className="mt-1 text-xs text-zinc-300/75">{o.welcome.headline}</div>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                      <div className="text-xs text-zinc-300/80">Retrait typique</div>
                      <div className="mt-1 font-extrabold text-white">~{o.payouts.typicalHours}h</div>
                      <div className="mt-1 text-xs text-zinc-300/75">{o.payouts.speedLabel}</div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {o.sports.slice(0, 4).map((s) => (
                      <span key={s} className="lbf-badge">
                        {s}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 flex flex-col gap-2">
                    <NavLink to={`/offres/${o.slug}`} className="lbf-btn-primary">
                      Lire l’avis <Icon icon={FiArrowRight} aria-hidden="true" />
                    </NavLink>
                    {o.links.official ? (
                      <a href={o.links.official} target="_blank" rel="noreferrer" className="lbf-btn-ghost">
                        Site officiel <Icon icon={FiExternalLink} aria-hidden="true" />
                      </a>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop/tablet table */}
            <div className="hidden md:block">
              <div className="lbf-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-[760px] w-full text-left text-sm">
                    <thead className="bg-white/5 text-xs uppercase tracking-wider text-zinc-300/80">
                      <tr>
                        <th className="px-5 py-4">Marque</th>
                        <th className="px-5 py-4">Bonus</th>
                        <th className="px-5 py-4">Retrait typique</th>
                        <th className="px-5 py-4">Sports</th>
                        <th className="px-5 py-4" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {rows.map((o) => (
                        <tr key={o.slug} className="bg-transparent hover:bg-white/[0.04] transition">
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <img
                                src={o.imagePath}
                                alt={o.brand}
                                className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 object-contain p-1"
                                loading="lazy"
                                decoding="async"
                              />
                              <div>
                                <div className="font-extrabold text-white">{o.brand}</div>
                                <div className="text-xs text-zinc-300/75">{o.tagline}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-semibold text-white/90">{o.welcome.maxEur}€</div>
                            <div className="text-xs text-zinc-300/75">{o.welcome.headline}</div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-semibold text-white/90">~{o.payouts.typicalHours}h</div>
                            <div className="text-xs text-zinc-300/75">{o.payouts.speedLabel}</div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex flex-wrap gap-2">
                              {o.sports.slice(0, 3).map((s) => (
                                <span key={s} className="lbf-badge">
                                  {s}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <NavLink to={`/offres/${o.slug}`} className="lbf-btn-primary">
                                Avis <Icon icon={FiArrowRight} aria-hidden="true" />
                              </NavLink>
                              {o.links.official ? (
                                <a
                                  href={o.links.official}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="lbf-btn-ghost px-3"
                                  aria-label={`Site officiel ${o.brand}`}
                                  title={`Site officiel ${o.brand}`}
                                >
                                  <Icon icon={FiExternalLink} aria-hidden="true" />
                                </a>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="lbf-card p-5">
                <div className="text-sm font-extrabold text-white">Choix rapide</div>
                <p className="mt-2 text-sm text-zinc-200/85">
                  Tu veux une interface ultra simple? Mets la note en priorité, puis vérifie la vitesse de retrait.
                </p>
              </div>
              <div className="lbf-card p-5">
                <div className="text-sm font-extrabold text-white">Choix “promo”</div>
                <p className="mt-2 text-sm text-zinc-200/85">
                  Si tu chasses les offres, regarde le calendrier. Les meilleures promos apparaissent souvent sur les
                  temps forts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}


